import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import NewItemDialog from "../modal/NewItemDialog";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import { isAxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { Textarea } from "../ui/textarea";
import { useCreateExhibition } from "@/api/exhibition/exhibition";
import {
  appendNoDuplicateExhibitionNumberRefine,
  exhibitionSchema,
} from "@/api/exhibition/exhibition-validation";

const schema = appendNoDuplicateExhibitionNumberRefine(
  exhibitionSchema.pick({
    expositionsDescriptionNumeroExpo: true,
    expositionsDescriptionTitre: true,
  }),
);

type FormType = z.infer<typeof schema>;

type NewExhibitionDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const NewExhibitionDialog = ({
  onOpenChange,
  open,
}: NewExhibitionDialogProps) => {
  const { t } = useTranslation();
  const form = useForm<FormType>({
    resolver: zodResolver(schema),
    reValidateMode: "onSubmit",
    defaultValues: {
      expositionsDescriptionNumeroExpo: "",
      expositionsDescriptionTitre: "",
    },
  });
  const { mutateAsync } = useCreateExhibition();
  const navigate = useNavigate();

  const onFormSubmit = async (data: FormType, shouldRedirect: boolean) => {
    try {
      const result = await mutateAsync(data);
      if (!shouldRedirect) {
        toast({
          title: t("newRecordSuccessfullyCreated"),
          duration: 3000,
          variant: "success",
        });
        form.reset();
        return;
      }
      navigate(`/edit/exhibition/${result.id}`);
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        form.setError("expositionsDescriptionNumeroExpo", {
          message: t("itemWithSameIdAlreadyExists"),
        });
        return;
      }
      form.setError("root", { message: t("unknownError") });
      throw err;
    }
  };

  return (
    <NewItemDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("createEexhibition")}
      onFormSubmit={onFormSubmit}
      form={form}
    >
      <FormField
        control={form.control}
        name="expositionsDescriptionNumeroExpo"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("expositionsDescriptionNumeroExpo")}</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="expositionsDescriptionTitre"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("expositionsDescriptionTitre")}</FormLabel>
            <FormControl>
              <Textarea {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormMessage>{form.formState.errors.root?.message}</FormMessage>
    </NewItemDialog>
  );
};

export default NewExhibitionDialog;
